import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { apiClient } from "@/lib/api";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | paid | failed | timeout
  const [attempts, setAttempts] = useState(0);
  const MAX_ATTEMPTS = 8;

  const poll = useCallback(async () => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    try {
      const res = await apiClient.get(`/payments/status/${sessionId}`);
      if (res.data.payment_status === "paid") {
        setStatus("paid");
        return;
      }
      if (res.data.status === "expired") {
        setStatus("failed");
        return;
      }
      if (attempts >= MAX_ATTEMPTS) {
        setStatus("timeout");
        return;
      }
      setTimeout(() => setAttempts((a) => a + 1), 2000);
    } catch {
      setStatus("failed");
    }
  }, [sessionId, attempts]);

  useEffect(() => { if (status === "checking") poll(); }, [poll, status]);

  return (
    <div data-testid="payment-success-page" className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-16">
      <div className="max-w-lg w-full bg-white border border-slate-200 rounded-3xl p-10 text-center shadow-xl">
        {status === "checking" && (
          <>
            <Loader2 className="w-14 h-14 text-[#1E3A8A] animate-spin mx-auto" />
            <h1 className="font-display uppercase text-3xl mt-4 text-slate-900">Confirming payment…</h1>
            <p className="text-slate-500 mt-2 text-sm">Please don't close this page. This usually takes a few seconds.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto" />
            <h1 className="font-display uppercase text-3xl mt-4 text-slate-900">Payment received</h1>
            <p className="text-slate-500 mt-3">Thanks! Your spot is confirmed. We've emailed your booking details. See you on the pitch!</p>
            <div className="mt-8 flex gap-3 justify-center">
              <Link to="/" className="btn-gold">Back to home</Link>
              <Link to="/private-sessions" className="btn-outline-blue">Book Again</Link>
            </div>
          </>
        )}
        {status === "timeout" && (
          <>
            <Loader2 className="w-14 h-14 text-amber-500 mx-auto" />
            <h1 className="font-display uppercase text-3xl mt-4 text-slate-900">Still processing</h1>
            <p className="text-slate-500 mt-3">Your payment is still being processed. We'll email you as soon as it's confirmed.</p>
            <button data-testid="payment-retry" onClick={() => { setAttempts(0); setStatus("checking"); }} className="btn-gold mt-6">Check again</button>
          </>
        )}
        {status === "failed" && (
          <>
            <XCircle className="w-16 h-16 text-rose-500 mx-auto" />
            <h1 className="font-display uppercase text-3xl mt-4 text-slate-900">Payment unsuccessful</h1>
            <p className="text-slate-500 mt-3">Something went wrong or the session expired. You can try again from the booking page.</p>
            <button data-testid="payment-back-home" onClick={() => navigate("/")} className="btn-gold mt-6">Back to home</button>
          </>
        )}
      </div>
    </div>
  );
}
